import { ListMask } from '@euler/app/components/list/ListMask';
import { MediaCoverView } from '@euler/app/components/media/MediaCoverView';
import { Typography } from '@euler/app/components/typography';
import { EmptyPlaceholder } from '@euler/app/tv/components/EmptyPlaceholder';
import { MediaList } from '@euler/app/tv/components/MediaList';
import { Panel } from '@euler/app/tv/components/Panel';
import { usePresentationTheme } from '@euler/app/tv/theme';
import { Colors } from '@euler/components/Colors';
import {
  LayoutProviderView,
  useContainerLayout,
} from '@euler/components/layout/LayoutProvider';
import { HStack } from '@euler/components/layout/Stack';
import { computeGridLayoutItemSize } from '@euler/functions/layoutMetrics';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { MediaObject } from '@euler/model';
import { usePersistFn } from '@euler/utils/hooks';
import { MotiView } from 'moti';
import { identity } from 'ramda';
import { ReactNode, memo, useCallback, useState } from 'react';
import {
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  StyleProp,
  StyleSheet,
  TouchableOpacity,
  TouchableWithoutFeedback,
  View,
  ViewStyle,
} from 'react-native';

const kCellMargin = 40;
const kMaxCellWidth = 208;

export const DetailRow = memo(
  ({
    title,
    medias,
    children,
    style,
    fallback,
    accessoryView,
    onPress,
  }: {
    title?: string;
    medias: MediaObject[];
    children?: ReactNode;
    style?: StyleProp<ViewStyle>;
    fallback?: ReactNode;
    accessoryView?: ReactNode;
    onPress?: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <Panel
        style={style}
        title={title}
        contentContainerStyle={{
          paddingTop: 0,
          paddingBottom: 0,
          paddingLeft: 0,
          paddingRight: 0,
        }}
      >
        <TouchableWithoutFeedback onPress={onPress}>
          <View style={StyleSheet.absoluteFill} />
        </TouchableWithoutFeedback>

        <HStack alignItems="stretch">
          {children != null && <>{children}</>}
          <View
            css={`
              flex: 1;
              padding: 0 ${rpx(10)}px;
            `}
          >
            <LayoutProviderView
              css={`
                flex: 1;
              `}
            >
              {medias.length > 0 ? (
                <HStack
                  alignItems="stretch"
                  css={`
                    flex: 1;
                  `}
                >
                  <MediaItems medias={medias} />
                  <>{accessoryView}</>
                </HStack>
              ) : (
                fallback ?? (
                  <EmptyPlaceholder
                    css={`
                      width: 100%;
                      height: 100%;
                    `}
                    text="暂无相关图片/视频"
                    iconSize={rpx(100)}
                    imageStyle={{ marginLeft: 0, left: -rpx(16) }}
                    textStyle={{
                      fontSize: rpx(20),
                      lineHeight: rpx(24),
                      marginTop: rpx(16),
                    }}
                  />
                )
              )}
            </LayoutProviderView>
          </View>
        </HStack>
      </Panel>
    );
  },
);

const MediaItems = memo(
  ({
    medias,
    style,
  }: {
    medias: MediaObject[];
    style?: StyleProp<ViewStyle>;
  }) => {
    const { rpx } = useResponsiveContext();
    const { width } = useContainerLayout();
    const cellMargin = rpx(kCellMargin);
    const maxCellWidth = rpx(kMaxCellWidth);
    const columns = Math.floor(
      (width + cellMargin) / (maxCellWidth + cellMargin),
    );
    const [cellWidth, cellHeight] = computeGridLayoutItemSize({
      numberOfColumns: columns + 0.25,
      gridWidth: width,
      cellMargin: { left: 0, top: 0, bottom: 0, right: cellMargin },
      aspectRatio: 208 / 156,
      gridPadding: 0,
    });

    const [showLeftMask, setShowLeftMask] = useState(false);

    const onScroll = usePersistFn(
      (e: NativeSyntheticEvent<NativeScrollEvent>) => {
        const offset = e.nativeEvent.contentOffset.x;
        const isLeftMaskVisible = offset > 0;
        if (showLeftMask !== isLeftMaskVisible) {
          setShowLeftMask(isLeftMaskVisible);
        }
      },
    );

    return (
      <ListMask
        leadingMask={showLeftMask}
        trailingMask
        css={`
          flex: 1;
        `}
      >
        <MediaList
          data={medias}
          mapToMediaObject={identity}
          keyExtractor={x => x.id}
          horizontal
          showsHorizontalScrollIndicator={Platform.OS === 'web'}
          contentContainerStyle={{
            flexGrow: 1,
            alignItems: 'center',
          }}
          scrollEventThrottle={1}
          onScroll={onScroll}
          renderItem={({ media, index, onPress }) => (
            <Cell
              media={media}
              index={index}
              onPress={onPress}
              cellWidth={cellWidth}
              cellHeight={cellHeight}
              css={`
                width: ${cellWidth}px;
                height: ${cellHeight}px;
                margin-right: ${cellMargin}px;
                overflow: hidden;
              `}
              mediaStyle={{
                width: cellWidth,
                height: cellHeight,
              }}
            />
          )}
          style={style}
        />
      </ListMask>
    );
  },
);

const Cell = memo(
  ({
    media,
    style,
    mediaStyle,
    index,
    cellWidth,
    cellHeight,
    onPress,
  }: {
    media: MediaObject;
    index: number;
    style?: StyleProp<ViewStyle>;
    mediaStyle?: StyleProp<ViewStyle>;
    cellWidth: number;
    cellHeight: number;
    onPress?: (media: MediaObject) => void;
  }) => {
    const {
      task: {
        scrollRow: {
          media: { titleColor },
        },
      },
    } = usePresentationTheme();

    const { rpx } = useResponsiveContext();

    const handlePress = useCallback(() => {
      onPress?.(media);
    }, [media, onPress]);

    return (
      <MotiView
        from={{
          opacity: 0,
          translateX: 100,
        }}
        animate={{
          opacity: 1,
          translateX: 0,
        }}
        transition={{
          type: 'timing',
          duration: 350,
        }}
        delay={Math.min(1500, index * 50)}
        style={style}
      >
        <MediaCoverView
          media={media}
          TouchableComponent={TouchableOpacity}
          onPress={handlePress}
          activityIndicatorSize={rpx(60)}
          noPlaceholder={
            media.type === 'video' ||
            (media.type === 'image' && media.url.includes('#'))
          }
          playIconSize={rpx(60)}
          playIconColor={Colors.White7_5}
          containerStyle={{
            borderRadius: rpx(20),
            overflow: 'hidden',
          }}
          imageStyle={{
            width: cellWidth,
            height: cellHeight,
          }}
          style={mediaStyle}
        />
        <View
          pointerEvents="none"
          css={`
            justify-content: center;
            margin-top: ${rpx(12)}px;
          `}
        >
          {media.title ? (
            <Typography.Label
              regular
              size={rpx(18)}
              color={titleColor}
              css={`
                text-align: center;
              `}
            >
              {media.subTitle
                ? `${media.title}: ${media.subTitle}`
                : media.title}
            </Typography.Label>
          ) : null}
        </View>
      </MotiView>
    );
  },
);
